import React, { useState } from "react";
import axiosClient from "../api/axiosClient";
import { useNavigate } from "react-router-dom";
import { setAuthToken, setRefreshToken } from "../utils/auth";
import { jwtDecode } from "jwt-decode";
import activityTracker from "../lib/activityTracker";
import { homePathForLevel } from "../utils/homePath";
import { FaUser, FaLock, FaSpinner } from "react-icons/fa";
import { Eye, EyeOff } from "lucide-react";
import logo from "../assets/vertice_logoHorizontal.png";

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    if (!username || !password) {
      setError("El usuario y la contraseña son obligatorios");
      setLoading(false);
      return;
    }

    try {
      const response = await axiosClient.post("/login", {
        username,
        password,
        app: "ventas",
      });

      if (!response || !response.token) {
        throw new Error("Token not found in response");
      }

      const { token } = response;
      localStorage.setItem("token", token);
      setAuthToken(token);
      if (response.refresh_token) {
        setRefreshToken(response.refresh_token);
      }
      let payload = null;
      try {
        payload = jwtDecode(token);
        activityTracker.init({
          user_id: payload.user_id,
          company_id: payload.company_id,
        });
        activityTracker.track("LOGIN", { method: "password" });
        activityTracker.track("APP_OPEN", { source: "login" });
      } catch (_) {
        /* noop */
      }
      navigate(homePathForLevel(payload?.level ?? null));
    } catch (err) {
      console.error("Error en el login:", err);
      const status = err?.response?.status;
      const isTimeout =
        err?.code === "ECONNABORTED" || /timeout/i.test(err?.message || "");
      const isNetwork =
        err?.code === "ERR_NETWORK" ||
        err?.message === "Network Error" ||
        (typeof navigator !== "undefined" && navigator.onLine === false);

      if (status === 400 || status === 401 || status === 403) {
        // El servidor sí respondió y rechazó las credenciales.
        setError("Usuario o contraseña incorrectos");
      } else if (typeof status === "number" && status >= 500) {
        setError(
          "El servidor no responde en este momento. Inténtalo de nuevo en unos minutos.",
        );
      } else if (isTimeout || isNetwork) {
        // No hubo respuesta del servidor: conexión lenta, caída o sin internet.
        setError(
          "No hemos podido conectar con el servidor. Revisa tu conexión a internet e inténtalo de nuevo.",
        );
      } else {
        setError("No se pudo iniciar sesión. Inténtalo de nuevo.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50 flex items-center justify-center p-4">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-red-400/20 to-red-400/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-red-400/20 to-red-400/20 rounded-full blur-3xl"></div>
      </div>

      {/* Login card */}
      <div className="relative bg-white/80 backdrop-blur-md rounded-3xl shadow-2xl border border-red-100 p-8 w-full max-w-md">
        {/* Logo/Branding */}
        <div className="text-center mb-8">
          <div className="relative inline-block">
            <img
              src={logo}
              alt="ConextCRM"
              className="w-64 h-auto mx-auto mb-2"
            />
            <span className="absolute top-0 -right-2 bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-md tracking-wider">
              VENTAS
            </span>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Login form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Username field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Usuario
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaUser className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                name="username"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 bg-white/70 backdrop-blur-sm"
                placeholder="Ingresa tu usuario"
                required
              />
            </div>
          </div>

          {/* Password field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Contraseña
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaLock className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="block w-full pl-10 pr-12 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 bg-white/70 backdrop-blur-sm"
                placeholder="Ingresa tu contraseña"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={
                  showPassword ? "Ocultar contraseña" : "Mostrar contraseña"
                }
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          {/* Submit button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center px-4 py-3 border-2 border-white text-base font-semibold rounded-full text-white bg-gradient-to-r from-red-600 to-red-600 hover:from-red-700 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-xl hover:shadow-2xl transform hover:-translate-y-0.5"
          >
            {loading ? (
              <>
                <FaSpinner className="animate-spin -ml-1 mr-3 h-5 w-5" />
                Iniciando sesión...
              </>
            ) : (
              "Iniciar sesión"
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-gray-500">
            © 2026 verticetrade. Todos los derechos reservados.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
